"use client"

/** Shared Sahil product carousel with pointer/touch drag and edge-aware arrow controls. */
import { ArrowLeft, ArrowRight } from "@medusajs/icons"
import {
  MouseEvent,
  PointerEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react"

type ProductCarouselProps = {
  children: ReactNode
  label?: string
}

export default function ProductCarousel({
  children,
  label = "Featured Sahil products",
}: ProductCarouselProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const dragStartX = useRef<number | null>(null)
  const dragStartScrollLeft = useRef(0)
  const didDrag = useRef(false)
  const animationFrame = useRef<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [canMovePrevious, setCanMovePrevious] = useState(false)
  const [canMoveNext, setCanMoveNext] = useState(false)

  const updateControlAvailability = () => {
    const viewport = viewportRef.current
    if (!viewport) return

    const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth
    setCanMovePrevious(viewport.scrollLeft > 3)
    setCanMoveNext(viewport.scrollLeft < maxScrollLeft - 3)
  }

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    const frame = window.requestAnimationFrame(updateControlAvailability)
    viewport.addEventListener("scroll", updateControlAvailability, {
      passive: true,
    })

    const resizeObserver = new ResizeObserver(updateControlAvailability)
    resizeObserver.observe(viewport)

    return () => {
      window.cancelAnimationFrame(frame)
      viewport.removeEventListener("scroll", updateControlAvailability)
      resizeObserver.disconnect()
    }
  }, [])

  useEffect(() => {
    return () => {
      if (animationFrame.current !== null) {
        window.cancelAnimationFrame(animationFrame.current)
      }
    }
  }, [])

  const getTrack = () =>
    viewportRef.current?.firstElementChild as HTMLElement | null

  const getCardOffsets = () => {
    const track = getTrack()
    if (!track) return []

    return Array.from(track.children)
      .filter((child): child is HTMLElement => child instanceof HTMLElement)
      .map((card) => card.offsetLeft)
  }

  const getTargetOffset = (direction: number) => {
    const viewport = viewportRef.current
    const offsets = getCardOffsets()
    if (!viewport || !offsets.length) return null

    const current = viewport.scrollLeft
    if (direction > 0) {
      return (
        offsets.find((offset) => offset > current + 3) ??
        offsets[offsets.length - 1]
      )
    }

    return (
      [...offsets].reverse().find((offset) => offset < current - 3) ??
      offsets[0]
    )
  }

  const animateTo = (target: number) => {
    const viewport = viewportRef.current
    if (!viewport) return

    if (animationFrame.current !== null) {
      window.cancelAnimationFrame(animationFrame.current)
    }

    const start = viewport.scrollLeft
    const distance = target - start
    if (Math.abs(distance) < 1) {
      updateControlAvailability()
      return
    }

    const startTime = window.performance.now()
    const duration = 420
    const frame = (time: number) => {
      const progress = Math.min((time - startTime) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      viewport.scrollLeft = start + distance * eased

      if (progress < 1) {
        animationFrame.current = window.requestAnimationFrame(frame)
      } else {
        animationFrame.current = null
        updateControlAvailability()
      }
    }

    animationFrame.current = window.requestAnimationFrame(frame)
  }

  const move = (direction: number) => {
    const target = getTargetOffset(direction)
    if (target !== null) animateTo(target)
  }

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return

    const viewport = viewportRef.current
    if (!viewport) return

    if (animationFrame.current !== null) {
      window.cancelAnimationFrame(animationFrame.current)
      animationFrame.current = null
    }

    dragStartX.current = event.clientX
    dragStartScrollLeft.current = viewport.scrollLeft
    didDrag.current = false
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current
    if (!viewport || dragStartX.current === null) return

    const distance = event.clientX - dragStartX.current
    if (Math.abs(distance) < 6) return

    event.preventDefault()
    if (!didDrag.current) {
      didDrag.current = true
      setIsDragging(true)
      viewport.setPointerCapture(event.pointerId)
    }

    viewport.style.scrollBehavior = "auto"
    viewport.scrollLeft = dragStartScrollLeft.current - distance
  }

  const settleAfterDrag = () => {
    const viewport = viewportRef.current
    if (!viewport || !didDrag.current) return

    const offsets = getCardOffsets()
    if (!offsets.length) return

    const target = offsets.reduce((closest, offset) =>
      Math.abs(offset - viewport.scrollLeft) <
      Math.abs(closest - viewport.scrollLeft)
        ? offset
        : closest
    )

    animateTo(target)
  }

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!didDrag.current) return
    event.preventDefault()
    event.stopPropagation()
    didDrag.current = false
  }

  const handlePointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current
    if (viewport?.hasPointerCapture(event.pointerId)) {
      viewport.releasePointerCapture(event.pointerId)
    }

    dragStartX.current = null
    setIsDragging(false)
    viewport?.style.removeProperty("scroll-behavior")
    settleAfterDrag()
  }

  return (
    <div className="sahil-product-carousel">
      <div
        ref={viewportRef}
        className={`sahil-product-carousel__viewport ${
          isDragging ? "is-dragging" : ""
        }`}
        aria-label={label}
        onClickCapture={handleClickCapture}
        onDragStart={(event) => event.preventDefault()}
        onPointerDownCapture={handlePointerDown}
        onPointerMoveCapture={handlePointerMove}
        onPointerUpCapture={handlePointerEnd}
        onPointerCancelCapture={handlePointerEnd}
      >
        {children}
      </div>

      <div
        className="sahil-product-carousel__controls"
        aria-label={`${label} carousel controls`}
      >
        {canMovePrevious && (
          <button
            type="button"
            className="sahil-rail-edge-control sahil-rail-edge-control--previous"
            onClick={() => move(-1)}
            aria-label={`Previous ${label.toLowerCase()}`}
          >
            <ArrowLeft aria-hidden="true" />
          </button>
        )}
        {canMoveNext && (
          <button
            type="button"
            className="sahil-rail-edge-control sahil-rail-edge-control--next"
            onClick={() => move(1)}
            aria-label={`Next ${label.toLowerCase()}`}
          >
            <ArrowRight aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  )
}
